import objectPath from 'object-path'
import { getAllKeys, getCurrentUi } from '../../Data'
import cloneDeep from 'clone-deep'

const getSyncDataValue = (data = {}, pathList = []) => {
  let result
  pathList.find((path) => {
    if (!path) return false
    if (objectPath.has(data, path)) {
      result = objectPath.get(data, path)
      return true
    }
    return false
  })
  return result
}

const getSyncDefaultValue = (ui = {}) => {
  let defaultValue
  if (objectPath.has(ui, 'defaultValue')) {
    defaultValue = objectPath.get(ui, 'defaultValue')
  } else if (objectPath.has(ui, 'input.defaultValue')) {
    defaultValue = objectPath.get(ui, 'input.defaultValue')
  } else if (objectPath.has(ui, 'defaultChecked')) {
    defaultValue = objectPath.get(ui, 'defaultChecked')
  }
  if (
    ui.tagname === 'select' &&
    defaultValue &&
    typeof defaultValue === 'object'
  ) {
    let optionType = ui.optionType
    let optionValue = objectPath.get(ui, `${optionType}.optionValue`) || 'value'
    if (Array.isArray(defaultValue)) {
      return defaultValue.map((item) => {
        if (item && typeof item === 'object') {
          return objectPath.get(item, optionValue)
        }
        return item
      })
    }
    return objectPath.get(defaultValue, optionValue)
  }
  return defaultValue
}

const getSyncFieldPathList = ({ syncFrom = {}, syncFromUi = {}, ui = {} }) => {
  let groupList = [
    syncFrom?.valueKey,
    syncFrom?.key,
    syncFromUi?.name,
    syncFromUi?.key,
  ].filter((key, index, list) => key && list.indexOf(key) === index)
  let fieldList = [ui.name, ui.key].filter(
    (key, index, list) => key && list.indexOf(key) === index
  )
  let pathList = []

  groupList.forEach((group) => {
    group = `${group}`.replace('@', '')
    fieldList.forEach((field) => {
      pathList.push(`@${group}.${field}`)
      pathList.push(`${group}.${field}`)
      pathList.push(`@${group}-${field}`)
      pathList.push(`${group}-${field}`)
    })
  })

  return pathList.filter(
    (path, index, list) => path && list.indexOf(path) === index
  )
}

const getSyncTargetFieldPathList = ({ syncTo = {}, syncToUi = {}, ui = {} }) =>
  getSyncFieldPathList({ syncFrom: syncTo, syncFromUi: syncToUi, ui })

const getSyncFromField = (syncFromUi = {}, jsonForm = {}) => {
  let { mapKey = {}, mapFormData = {} } = jsonForm.schema || {}
  return Object.keys(mapKey).filter((key) => {
    if (key === syncFromUi.key) return false
    let fullKey = mapKey[key] || ''
    let keyList = fullKey.split('.')
    return keyList.includes(syncFromUi.key) && mapFormData[key]
  })
}

const getSyncFromData = (syncFromData, action, designContext) => {
  const { jsonForm } = designContext
  const { syncFrom } = action
  if (Array.isArray(syncFromData)) return syncFromData

  let syncFromUi = getCurrentUi(
    syncFrom?.key,
    jsonForm.schema,
    jsonForm.uischema
  )
  if (syncFromUi.key !== syncFrom?.key) return syncFromData
  if (syncFromData && typeof syncFromData !== 'object') return syncFromData

  let formData = jsonForm.formData
  let fromData = syncFromData ? cloneDeep(syncFromData) : {}
  let hasSyncValue = false
  let defaultField = getSyncFromField(syncFromUi, jsonForm)
  defaultField.forEach((key) => {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    if (ui.key !== key) return

    let value = getSyncDataValue(fromData, [ui.name, ui.key])
    if (value === undefined) {
      value = getSyncDataValue(
        formData,
        getSyncFieldPathList({ syncFrom, syncFromUi, ui })
      )
    }
    if (value === undefined) {
      value = getSyncDefaultValue(ui)
    }
    if (value === undefined) {
      value = getSyncDataValue(formData, [ui.name, ui.key])
    }
    if (value !== undefined) {
      objectPath.set(fromData, ui.name, value)
      hasSyncValue = true
    }
  })

  if (!syncFromData && !hasSyncValue) return syncFromData
  return fromData
}

const getSyncContainerField = (syncToUi = {}, jsonForm = {}) => {
  let { mapKey, mapFormData } = jsonForm.schema
  return Object.keys(mapKey).filter((key) => {
    let fullKey = mapKey[key] || ''
    let keyList = fullKey.split('.')
    return keyList.includes(syncToUi.key) && mapFormData[key]
  })
}

const getSyncTargetData = (
  { syncToData = {}, defaultField = [] },
  designContext
) => {
  const { jsonForm } = designContext
  let formData = jsonForm.formData
  let toData =
    syncToData && typeof syncToData === 'object' ? cloneDeep(syncToData) : {}

  defaultField.forEach((key) => {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    if (ui.key !== key) return

    let value = getSyncDataValue(toData, [ui.name, ui.key])
    if (value === undefined) {
      value = getSyncDefaultValue(ui)
    }
    if (value === undefined) {
      value = getSyncDataValue(formData, [ui.name, ui.key])
    }
    if (value !== undefined && value !== '') {
      objectPath.set(toData, ui.name, value)
    }
  })

  return toData
}

const onSyncArrayData = (
  { syncFromData, syncToUi, syncToData },
  action,
  designContext
) => {
  const { jsonForm, setFieldFormData, setFormData } = designContext
  let formData = jsonForm.formData
  const { refField, syncTo } = action
  let dataSync = {}
  let defaultField = Object.keys(syncFromData)
  if (syncToUi.tagname === 'dynamic') {
    if (syncToData) {
      if (Array.isArray(syncToData)) {
        let foundIndex = syncToData.findIndex((data) => {
          if (!refField) return false
          let from = objectPath.get(syncFromData, refField)
          let to = objectPath.get(data, refField)
          return isSameSyncRefValue(from, to)
        })
        if (foundIndex > -1) {
          defaultField.forEach((name) => {
            let value = objectPath.get(syncFromData, name)
            if (value !== undefined) {
              objectPath.set(syncToData, `${foundIndex}.${name}`, value)
            }
          })
        } else {
          defaultField.forEach((name) => {
            let value = objectPath.get(syncFromData, name)
            if (value !== undefined) {
              objectPath.set(dataSync, name, value)
            }
          })
          syncToData.push(dataSync)
        }
      }
    } else {
      syncToData = []
      defaultField.forEach((name) => {
        let value = objectPath.get(syncFromData, name)
        if (value !== undefined) {
          objectPath.set(dataSync, name, value)
        }
      })
      syncToData.push(dataSync)
    }
    let syncToName = syncTo?.valueKey
    let syncToKey = syncTo?.key
    setFieldFormData(syncToName, syncToData)
    setFieldFormData(syncToKey, syncToData)
  } else if (syncToUi.uiType === 'container') {
    let { mapKey, mapFormData } = jsonForm.schema
    let defaultField = Object.keys(mapKey).filter((key) => {
      let fullKey = mapKey[key] || ''
      let keyList = fullKey.split('.')
      return keyList.includes(syncToUi.key) && mapFormData[key]
    })
    defaultField.forEach((key) => {
      let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      if (ui.key === key) {
        let value = objectPath.get(syncFromData, ui.name)
        if (value !== undefined) {
          objectPath.set(formData, key, value)
          objectPath.set(formData, ui.name, value)
        }
      }
    })
    setFormData(formData)
  }
}

const getSyncRefValue = (value) => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'object') {
    return (
      value.value ||
      value.code ||
      value.key ||
      value.name ||
      value.label ||
      ''
    )
  }

  return value
}

const isSameSyncRefValue = (from, to) => {
  let fromValue = getSyncRefValue(from)
  let toValue = getSyncRefValue(to)

  if (!fromValue || !toValue) return false

  return String(fromValue).toLowerCase() === String(toValue).toLowerCase()
}

const getSyncTargetRefValue = (syncToData = {}, action = {}) => {
  const { refField, syncTo } = action
  let refValue = objectPath.get(syncToData, refField)

  if (refValue === undefined) {
    refValue =
      action.value ||
      action.syncName ||
      syncTo?.value ||
      syncTo?.syncName
  }

  return refValue
}

const isSyncDynamicDataRefMatched = (
  syncFromData,
  syncToData,
  action
) => {
  const { refField } = action
  if (!refField) return true

  let fromRefValue = objectPath.get(syncFromData, refField)
  let toRefValue = getSyncTargetRefValue(syncToData, action)

  return isSameSyncRefValue(fromRefValue, toRefValue)
}

const onSyncDynamicData = (
  { syncFromData, syncToUi, syncToData },
  action,
  designContext
) => {
  const { jsonForm, setFormData } = designContext
  const { syncTo } = action
  let formData = jsonForm.formData
  if (syncToUi.uiType === 'container' && syncToUi.tagname !== 'dynamic') {
    let defaultField = getSyncContainerField(syncToUi, jsonForm)
    let targetData = getSyncTargetData(
      { syncToData, defaultField },
      designContext
    )
    let found = isSyncDynamicDataRefMatched(
      syncFromData,
      targetData,
      action
    )
    if (found) {
      defaultField.forEach((key) => {
        let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
        if (ui.key === key) {
          let value = objectPath.get(syncFromData, ui.name)
          if (value !== undefined) {
            objectPath.set(formData, key, value)
            objectPath.set(formData, ui.name, value)
            if (syncTo?.valueKey) {
              getSyncTargetFieldPathList({ syncTo, syncToUi, ui }).forEach(
                (path) => {
                  objectPath.set(formData, path, value)
                }
              )
            }
          }
        }
      })
    }
  }
  setFormData(formData)
}

const onSyncWidgetData = (
  { syncFromData, syncFromUi, syncToUi, syncToData = {} },
  action,
  designContext
) => {
  const {
    jsonForm,
    setFieldFormData,
    setFormData,
    widgetDesign,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const { exceptField } = action
  if (syncFromUi.tagname === 'widget' && syncToUi.tagname === 'widget') {
    let fromWidget = objectPath.get(widgetDesign, syncFromUi.path)
    let toWidget = objectPath.get(widgetDesign, syncToUi.path)
    let exceptList = exceptField.replace(' ', '').split(',')
    if (fromWidget && toWidget) {
      let widgetField = getAllKeys(syncFromData)
      widgetField.forEach((name) => {
        let value = objectPath.get(syncFromData, name)
        let isExcept = exceptList.includes(name)
        if (isExcept) return
        objectPath.set(syncToData, name, value)
        objectPath.set(isUiChanged, [syncToUi.name, name], true)
        objectPath.set(isUiChanged, [syncToUi.name, '@data', name], value)
      })
      if (syncToUi.isGroupValue) {
        setFieldFormData(syncToUi.name, syncToData)
        setFieldFormData(syncToUi.key, syncToData)
      } else {
        setFormData({ ...jsonForm.formData, ...syncToData })
      }
    }
    objectPath.set(isUiChanged, syncToUi.key, { isRender: true })
    setIsUiChanged(isUiChanged)
  }
}

const checkSyncData = (syncFromData, action, designContext, status) => {
  const {
    isUiChanged,
    setIsUiChanged,
    jsonForm,
    widgetDesign,
    setFieldFormData,
  } = designContext
  let formData = jsonForm.formData
  const { isArray, isDynamic, arrayKey } = status
  const { syncFrom, syncTo, exceptField = '' } = action
  let syncFromUi = getCurrentUi(
    syncFrom?.key,
    jsonForm.schema,
    jsonForm.uischema
  )
  let syncToKey = syncTo?.key
  let syncToName = syncTo?.valueKey
  let syncToData
  if (objectPath.has(formData, `@${syncToName}`)) {
    syncToData = objectPath.get(formData, `@${syncToName}`)
  } else if (objectPath.has(formData, syncToName)) {
    syncToData = objectPath.get(formData, syncToName)
  } else {
    syncToData = objectPath.get(formData, syncToKey)
  }

  syncToData = cloneDeep(syncToData)
  let syncToUi = getCurrentUi(syncToKey, jsonForm.schema, jsonForm.uischema)

  if (syncToUi.key !== syncToKey) {
    if (isArray) {
      objectPath.del(isUiChanged, `syncData.${arrayKey}`)
      setIsUiChanged(isUiChanged)
    }
    if (syncTo?.widgetKey) {
      let widgetUi = getCurrentUi(
        syncTo?.widgetKey,
        jsonForm.schema,
        jsonForm.uischema
      )
      if (widgetUi.key === syncTo?.widgetKey) {
        let widgetJsonForm = objectPath.get(widgetDesign, widgetUi.path)
        let fieldUi = getCurrentUi(
          syncToKey,
          widgetJsonForm.schema,
          widgetJsonForm.uischema
        )
        if (widgetUi.isGroupValue) {
          let fullName = `${widgetUi.name}.${fieldUi.name}`
          setFieldFormData(fullName, syncToData)
        } else {
          setFieldFormData(fieldUi.name, syncToData)
          setFieldFormData(fieldUi.key, syncToData)
        }
      }
    }
    if (syncTo?.groupType === 'formGroup') {
      let exceptList = exceptField.replace(' ', '').split(',')
      let fromData = cloneDeep(syncFromData)
      exceptList.forEach((key) => {
        objectPath.del(fromData, key)
      })
      let toData = { ...syncToData, ...fromData }
      setFieldFormData(syncToName, toData)
    } else if (syncTo?.isFormComponent) {
      let name = `${syncTo.formComponentName}.${syncToName}`
      setFieldFormData(name, syncFromData)
    }
    return
  }
  let isWidget = syncFromUi.tagname === 'widget'
  if (isArray) {
    onSyncArrayData(
      { syncFromData, syncToUi, syncToData },
      action,
      designContext
    )
  } else if (isDynamic) {
    onSyncDynamicData(
      { syncFromData, syncToUi, syncToData },
      action,
      designContext
    )
  } else if (isWidget) {
    onSyncWidgetData(
      { syncFromData, syncFromUi, syncToUi, syncToData },
      action,
      designContext
    )
  } else {
    setFieldFormData(syncToKey, syncFromData)
    setFieldFormData(syncToName, syncFromData)
  }
}

export const onSyncData = (
  action,
  data,
  elementAction,
  designContext,
  status
) => {
  const { isUiChanged, setIsUiChanged, jsonForm } = designContext
  let formData = jsonForm.formData
  const { isArray, isDynamic, isWidget, widgetName, arrayKey, dynamicIndex } =
    status
  const { syncFrom, syncTo } = action
  let syncFromName = syncFrom?.valueKey

  if (syncFromName) {
    let syncFromKey = syncFrom?.key
    let syncFromData
    if (syncFrom.isFormComponent) {
      let { formComponentName } = syncFrom
      syncFromData = objectPath.get(
        formData,
        `${formComponentName}.${syncFromName}`
      )
    } else if (syncFrom?.groupType === 'formGroup') {
      syncFromData = objectPath.get(formData, syncFromName)
    } else if (isWidget) {
      syncFromData = objectPath.get(formData, [widgetName])
    } else if (isDynamic) {
      syncFromData = objectPath.get(formData, [syncFromName, dynamicIndex])
    } else if (objectPath.has(data, `@${syncFromName}`)) {
      syncFromData = objectPath.get(data, `@${syncFromName}`)
    } else if (objectPath.has(data, syncFromName)) {
      syncFromData = objectPath.get(data, syncFromName)
    } else {
      syncFromData = objectPath.get(data, syncFromKey)
    }
    syncFromData = getSyncFromData(syncFromData, action, designContext)
    if (syncTo && syncFromData) {
      checkSyncData(syncFromData, action, designContext, status)
    }
  }
  if (isArray) {
    objectPath.del(isUiChanged, `syncData.${arrayKey}`)
  }
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}
